(function () {
  'use strict';

  /* ============================================================
   * CWSCarouselLib — Vanilla JS carousel for Shopify sections
   *
   * Singleton guard: if this script is included more than once
   * on a page (e.g. multiple Shopify sections sharing the asset)
   * the second execution simply calls initAll() on the already-
   * defined lib and returns.  Every carousel element tracks its
   * own isolated state — no cross-instance contamination.
   *
   * Usage (HTML):
   *   <div class="cws-carousel"
   *        data-cws-options='{
   *          "slidesToShow": 4,
   *          "slidesToScroll": 1,
   *          "dots": true,
   *          "arrows": true,
   *          "gap": 16,
   *          "breakpoints": [
   *            { "maxWidth": 1024, "slidesToShow": 2, "slidesToScroll": 1 },
   *            { "maxWidth": 768,  "slidesToShow": 1, "slidesToScroll": 1 }
   *          ]
   *        }'>
   *     <div>Slide 1</div>
   *     <div>Slide 2</div>
   *   </div>
   *
   * Usage (JS):
   *   CWSCarouselLib.init(el, options)  — init a single element
   *   CWSCarouselLib.initAll()          — init all .cws-carousel on page
   *   CWSCarouselLib.get(el)            — retrieve an existing instance
   *   CWSCarouselLib.destroy(el)        — teardown a single instance
   *
   * Shopify section:load support:
   *   document.addEventListener('shopify:section:load', () => {
   *     CWSCarouselLib.initAll();
   *   });
   * ============================================================ */

  /* ── Singleton guard ───────────────────────────────────────── */
  if (window.CWSCarouselLib) {
    window.CWSCarouselLib.initAll();
    return;
  }

  /* ----------------------------------------------------------
   * CWSCarousel — one isolated instance per carousel element
   * ---------------------------------------------------------- */
  class CWSCarousel {
    constructor(el) {
      this.el           = el;
      this.options      = this._parseOptions();
      this.currentSlide = 0;
      this._slideWidth  = 0;   // step width in px (visual slide width + gap)
      this._active      = {};  // settings resolved for the current breakpoint
      this._resizeTimer = null;

      this._build();
      this._resolveBreakpoint(); // sets _active, runs _applyLayout → _render
      this._bindEvents();
      this.el.classList.add('cws-carousel--initialized');
    }

    /* ── Options ─────────────────────────────────────────────── */

    _parseOptions() {
      const defaults = {
        slidesToShow:   1,
        slidesToScroll: 1,
        arrows:         true,
        dots:           true,
        gap:            0,      // px gap between slides
        breakpoints:    [],     // [{ maxWidth, slidesToShow, slidesToScroll, arrows, dots, gap }]
      };
      let user = {};
      try {
        const raw = this.el.dataset.cwsOptions;
        if (raw) user = JSON.parse(raw);
      } catch (e) {
        console.warn('[CWSCarousel] Could not parse data-cws-options JSON on element:', this.el, e);
      }
      return Object.assign({}, defaults, user);
    }

    /* ── DOM construction ────────────────────────────────────── */

    _build() {
      // Capture only visible content children — exclude <script>, <style>, <link>,
      // <meta> etc. that Shopify snippets inject as siblings of the real slides.
      const EXCLUDED = new Set(['script', 'style', 'link', 'meta', 'noscript', 'template']);
      const slides = Array.from(this.el.children).filter(
        el => !EXCLUDED.has(el.tagName.toLowerCase())
      );

      // Track wrapper — the overflow clip boundary
      this._trackWrapper = document.createElement('div');
      this._trackWrapper.className = 'cws-carousel__track-wrapper';

      // Track — the element that physically translates
      this._track = document.createElement('div');
      this._track.className = 'cws-carousel__track';

      slides.forEach(slide => {
        slide.classList.add('cws-carousel__slide');
        this._track.appendChild(slide);
      });

      this._trackWrapper.appendChild(this._track);
      this.el.insertBefore(this._trackWrapper, this.el.firstChild);

      // Arrows
      this._prevBtn = this._makeArrow('prev');
      this._nextBtn = this._makeArrow('next');
      this.el.appendChild(this._prevBtn);
      this.el.appendChild(this._nextBtn);

      // Dots container — populated in _buildDots()
      this._dotsContainer = document.createElement('div');
      this._dotsContainer.className = 'cws-carousel__dots';
      this.el.appendChild(this._dotsContainer);
    }

    _makeArrow(dir) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = `cws-carousel__arrow cws-carousel__arrow--${dir}`;
      btn.setAttribute('aria-label', dir === 'prev' ? 'Previous slide' : 'Next slide');
      btn.innerHTML = dir === 'prev'
        ? `<svg viewBox="0 0 8 14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
             <path d="M7 1 1 7l6 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
           </svg>`
        : `<svg viewBox="0 0 8 14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
             <path d="M1 1l6 6-6 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
           </svg>`;
      return btn;
    }

    /* ── Breakpoint resolution ───────────────────────────────── */

    _resolveBreakpoint() {
      const w = window.innerWidth;

      // Find the tightest breakpoint whose maxWidth still fits the viewport
      const bp = [...(this.options.breakpoints || [])]
        .sort((a, b) => a.maxWidth - b.maxWidth)
        .find(b => w <= b.maxWidth);

      this._active = {
        slidesToShow:   bp?.slidesToShow   ?? this.options.slidesToShow,
        slidesToScroll: bp?.slidesToScroll ?? this.options.slidesToScroll,
        arrows:         bp?.arrows         ?? this.options.arrows,
        dots:           bp?.dots           ?? this.options.dots,
        gap:            bp?.gap            ?? this.options.gap,
      };

      this._applyLayout();
    }

    _applyLayout() {
      const { arrows, dots } = this._active;

      const showArrows = arrows && this._track.children.length > this._active.slidesToShow;
      this._prevBtn.style.display       = showArrows ? '' : 'none';
      this._nextBtn.style.display       = showArrows ? '' : 'none';
      this._dotsContainer.style.display = dots   ? '' : 'none';

      // Clamp currentSlide into the valid range for the new settings
      this.currentSlide = Math.min(this.currentSlide, this._maxSlide());

      this._buildDots();
      this._render();
    }

    /* ── Dot management ──────────────────────────────────────── */

    _buildDots() {
      this._dotsContainer.innerHTML = '';
      const n = this._dotCount();
      for (let i = 0; i < n; i++) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'cws-carousel__dot';
        btn.setAttribute('aria-label', `Go to slide group ${i + 1}`);
        btn.dataset.dotIndex = i;
        this._dotsContainer.appendChild(btn);
      }
    }

    _dotCount() {
      const total = this._track.children.length;
      const { slidesToShow, slidesToScroll } = this._active;
      if (total <= slidesToShow) return 0;
      return Math.ceil((total - slidesToShow) / slidesToScroll) + 1;
    }

    /* ── Navigation ──────────────────────────────────────────── */

    _maxSlide() {
      return Math.max(0, this._track.children.length - this._active.slidesToShow);
    }

    goTo(index) {
      this.currentSlide = Math.max(0, Math.min(index, this._maxSlide()));
      this._render();
    }

    next() { this.goTo(this.currentSlide + this._active.slidesToScroll); }
    prev() { this.goTo(this.currentSlide - this._active.slidesToScroll); }

    /* ── Render ──────────────────────────────────────────────── */

    _setSlideDimensions() {
      const { slidesToShow, gap } = this._active;
      const slides    = Array.from(this._track.children);
      const total     = slides.length;
      const wrapWidth = this._trackWrapper.offsetWidth;

      // Divide available width (minus inter-slide gaps) equally across visible slides
      const slideWidth = (wrapWidth - gap * (slidesToShow - 1)) / slidesToShow;

      // _slideWidth is the unit of movement: one slide's visual width + one gap
      this._slideWidth = slideWidth + gap;

      slides.forEach((slide, i) => {
        slide.style.width       = slideWidth + 'px';
        slide.style.marginRight = i < total - 1 ? gap + 'px' : '0';
      });

      // Track must accommodate all slides plus inter-slide gaps
      this._track.style.width = (slideWidth * total + gap * (total - 1)) + 'px';
    }

    _render() {
      this._setSlideDimensions();

      // Translate the track by the accumulated slide steps
      const offset = -(this.currentSlide * this._slideWidth);
      this._track.style.transform = `translateX(${offset}px)`;

      // Sync active dot
      const activeDot = Math.round(this.currentSlide / this._active.slidesToScroll);
      Array.from(this._dotsContainer.children).forEach((dot, i) => {
        dot.classList.toggle('cws-carousel__dot--active', i === activeDot);
      });

      // Sync arrow disabled state
      const atStart = this.currentSlide <= 0;
      const atEnd   = this.currentSlide >= this._maxSlide();
      this._prevBtn.disabled = atStart;
      this._nextBtn.disabled = atEnd;
      this._prevBtn.classList.toggle('cws-carousel__arrow--disabled', atStart);
      this._nextBtn.classList.toggle('cws-carousel__arrow--disabled', atEnd);
    }

    /* ── Events ──────────────────────────────────────────────── */

    _bindEvents() {
      this._prevBtn.addEventListener('click', () => this.prev());
      this._nextBtn.addEventListener('click', () => this.next());

      // Delegated dot listener — one handler for the whole dots container
      this._dotsContainer.addEventListener('click', e => {
        const btn = e.target.closest('.cws-carousel__dot');
        if (!btn) return;
        this.goTo(parseInt(btn.dataset.dotIndex, 10) * this._active.slidesToScroll);
      });

      // Debounced resize — stored as own property so destroy() can remove it
      this._onResize = () => {
        clearTimeout(this._resizeTimer);
        this._resizeTimer = setTimeout(() => this._resolveBreakpoint(), 150);
      };
      window.addEventListener('resize', this._onResize);
    }

    /* ── Teardown ────────────────────────────────────────────── */

    destroy() {
      window.removeEventListener('resize', this._onResize);
      clearTimeout(this._resizeTimer);
      this.el.classList.remove('cws-carousel--initialized');
    }
  }

  /* ----------------------------------------------------------
   * CWSCarouselLib — singleton registry, co-ordinates all instances
   * ---------------------------------------------------------- */
  const CWSCarouselLib = {
    _instances: new Map(),

    /**
     * Initialise a single element.
     * `options` passed here are merged on top of data-cws-options (JS wins).
     * Returns the CWSCarousel instance.
     */
    init(el, options = {}) {
      if (!(el instanceof Element)) return null;
      if (this._instances.has(el)) return this._instances.get(el);

      // Merge JS options over any existing JSON attribute
      if (Object.keys(options).length) {
        let base = {};
        try { base = JSON.parse(el.dataset.cwsOptions || '{}'); } catch (_) {}
        el.dataset.cwsOptions = JSON.stringify(Object.assign(base, options));
      }

      const instance = new CWSCarousel(el);
      this._instances.set(el, instance);
      return instance;
    },

    /**
     * Auto-initialise every .cws-carousel that hasn't been initialised yet
     * under an optional root element (defaults to the full document).
     * Safe to call multiple times — already-initialised elements are skipped
     * via the .cws-carousel--initialized guard class.
     */
    initAll(root = document) {
      root.querySelectorAll('.cws-carousel:not(.cws-carousel--initialized)')
          .forEach(el => this.init(el));
    },

    /** Retrieve the CWSCarousel instance for an element, or null. */
    get(el) {
      return this._instances.get(el) || null;
    },

    /** Destroy and unregister the instance for an element. */
    destroy(el) {
      const inst = this._instances.get(el);
      if (inst) {
        inst.destroy();
        this._instances.delete(el);
      }
    },
  };

  window.CWSCarouselLib = CWSCarouselLib;

  /* Auto-init on DOM ready */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => CWSCarouselLib.initAll());
  } else {
    CWSCarouselLib.initAll();
  }

})();
